import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { supabase } from './supabaseClient'
import Home from './pages/Home'
import BrowseLures from './pages/BrowseLures'
import LureDetail from './pages/LureDetail'
import TopRanked from './pages/TopRanked'
import Login from './pages/Login'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import MyCollection from './pages/MyCollection'
import AdminLayout from './components/AdminLayout'
import AdminDashboard from './pages/admin/AdminDashboard'
import AddLure from './pages/admin/AddLure'
import AddVariants from './pages/admin/AddVariants'
import ManageBrands from './pages/admin/ManageBrands'
import ManageLures from './pages/admin/ManageLures'
import EditLure from './pages/admin/EditLure'
import ManageVariants from './pages/admin/ManageVariants'
import MyProfile from './pages/MyProfile'
import UserProfile from './pages/UserProfile'
import Community from './pages/Community'
import Activity from './pages/Activity'
import MySubmissions from './pages/MySubmissions'
import UserSubmissionsHub from './pages/UserSubmissionsHub'
import ManageCompleteLureSubmissions from './pages/admin/ManageCompleteLureSubmissions'
import HitLists from './pages/HitLists'
import HitListDetail from './pages/HitListDetail'
import MyHitLists from './pages/MyHitLists'
import LureDiscussions from './pages/LureDiscussions'
import LureThread from './pages/LureThread'
import Forums from './pages/Forums'
import Messages from './pages/Messages'
import Conversation from './pages/Conversation'
import BrowseTrade from './pages/BrowseTrade'
import ProposeTrade from './pages/ProposeTrade'

import './App.css'

function AppContent() {
  const { user, signOut } = useAuth()
  const [unreadCount, setUnreadCount] = useState(0)

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (err) {
      console.error('Sign out error:', err)
    }
    window.location.href = '/'
  }

  const fetchUnreadCount = async () => {
    if (!user) { setUnreadCount(0); return }
    const { count } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('is_read', false)
      .neq('sender_id', user.id)
      .neq('is_system_message', true)
    setUnreadCount(count || 0)
  }

  useEffect(() => {
    if (!user) { setUnreadCount(0); return }
    fetchUnreadCount()

    const channel = supabase
      .channel('global-unread')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, fetchUnreadCount)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'messages' }, fetchUnreadCount)
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [user])

  return (
    <div className="App">
      {/* BGG-style Header */}
      <header className="bgg-header">
        <div className="header-top">
          <Link to="/" className="site-logo">
            <img 
              src="/fishinwiseguys-logo.png" 
              alt="FishinWiseGuys Logo" 
              className="logo-image"
            />
            <div className="logo-text">
              <h1>FishinWiseGuys</h1>
              <p className="tagline">The ultimate fishing gear database</p>
            </div>
          </Link>
          <div className="header-right">
            {user ? (
              <div className="user-menu">
                <Link to="/my-profile" className="profile-link">
                  👤 {user.email.split('@')[0]}
                </Link>
                <Link to="/messages" className="btn-messages">
                  ✉️{unreadCount > 0 && <span className="header-unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
                </Link>
                <button onClick={handleSignOut} className="btn-signout">
                  Sign Out
                </button>
              </div>
            ) : (
              <Link to="/login" className="btn-login">
                Login / Sign Up
              </Link>
            )}
            <a 
              href="https://www.youtube.com/@FishinWiseguys" 
              target="_blank" 
              rel="noopener noreferrer"
              className="youtube-link"
            >
              📺 YouTube Channel
            </a>
          </div>
        </div>
        
        {/* BGG-style Navigation - CONSOLIDATED LINK */}
        <nav className="main-nav">
          <Link to="/" className="nav-link">Home</Link>
          <Link to="/browse" className="nav-link">Browse Lures</Link>
          <Link to="/top-ranked" className="nav-link">Top Ranked</Link>
          <Link to="/community" className="nav-link">Community</Link>
          <Link to="/activity" className="nav-link">Activity</Link>
          {user && (
            <Link to="/submit" className="nav-link">🎣 Submit New Lures to Database</Link>
          )}
          <Link to="/forums" className="nav-link">Forums</Link>
          <Link to="/hitlists" className="nav-link">HitLists</Link>
          <Link to="/trade/browse" className="nav-link">Trade</Link>
          {user ? (
            <Link to="/my-collection" className="nav-link">My Collection</Link>
          ) : (
            <span className="nav-link disabled">My Collection (Login Required)</span>
          )}
        </nav>
      </header>

      {/* Main Content Area */}
      <main className="main-container">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/browse" element={<BrowseLures />} />
          <Route path="/lure/:id" element={<LureDetail />} />
          <Route path="/forums" element={<Forums />} />
          <Route path="/lure/:id/discussions" element={<LureDiscussions />} />
          <Route path="/lure/:id/discussions/:threadId" element={<LureThread />} />
          <Route path="/top-ranked" element={<TopRanked />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/my-collection" element={<MyCollection />} />
          <Route path="/my-profile" element={<MyProfile />} />
          <Route path="/profile/:userId" element={<UserProfile />} />
          <Route path="/activity" element={<Activity />} />
          <Route path="/community" element={<Community />} />
          
          {/* User Submission Routes - CONSOLIDATED */}
          <Route path="/submit" element={<UserSubmissionsHub />} />
          <Route path="/my-submissions" element={<MySubmissions />} />

          {/* Messaging */}
          <Route path="/messages" element={<Messages />} />
          <Route path="/messages/:conversationId" element={<Conversation />} />
          <Route path="/trade/browse" element={<BrowseTrade />} />
          <Route path="/trade/propose/:userId" element={<ProposeTrade />} />

          {/* HitLists */}
          <Route path="/hitlists" element={<HitLists />} />
          <Route path="/hitlists/:id" element={<HitListDetail />} />
          <Route path="/my-hitlists" element={<MyHitLists />} />
         
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="add-lure" element={<AddLure />} />
            <Route path="add-variants/:lureId" element={<AddVariants />} />
            <Route path="manage-brands" element={<ManageBrands />} />
            <Route path="manage-lures" element={<ManageLures />} />
            <Route path="edit-lure/:lureId" element={<EditLure />} />
            <Route path="manage-variants/:lureId" element={<ManageVariants />} />
            <Route path="manage-complete-submissions" element={<ManageCompleteLureSubmissions />} />
          </Route>
        </Routes>
      </main>

      {/* BGG-style Footer */}
      <footer className="bgg-footer">
        <div className="footer-content">
          <p>FishinWiseGuys © 2024 | Inspired by BoardGameGeek</p>
          <p className="footer-note">
            Building the ultimate community-driven fishing gear database
          </p>
        </div>
      </footer>
    </div>
  )
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  )
}

export default App